// client/src/components/admin/EventActionsDropdown.js
import React, { useEffect, useRef } from 'react'
import { Dropdown } from 'bootstrap'

/**
 * Dropdown menu for telemetry event actions.
 * Provides options to view details, copy ID/payload, and navigate to user.
 *
 * @param {Object} event - The telemetry event object
 * @param {Function} onViewDetails - Handler for viewing event details
 * @param {Function} onCopyId - Handler for copying event ID
 * @param {Function} onCopyPayload - Handler for copying event payload JSON
 * @param {Function} onJumpToUser - Handler for navigating to user profile
 */
function EventActionsDropdown({
  event,
  onViewDetails,
  onCopyId,
  onCopyPayload,
  onJumpToUser,
}) {
  const dropdownRef = useRef(null)
  const dropdownInstanceRef = useRef(null)

  useEffect(() => {
    // Manually initialize Bootstrap dropdown for mobile compatibility
    if (dropdownRef.current && !dropdownInstanceRef.current) {
      try {
        dropdownInstanceRef.current = new Dropdown(dropdownRef.current, {
          autoClose: true,
          boundary: 'viewport',
          offset: [0, 8],
        })
      } catch (error) {
        console.error('Failed to initialize dropdown:', error)
      }
    }

    return () => {
      if (dropdownInstanceRef.current) {
        try {
          dropdownInstanceRef.current.dispose()
          dropdownInstanceRef.current = null
        } catch (error) {
          console.error('Failed to dispose dropdown:', error)
        }
      }
    }
  }, [])

  const handleToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (dropdownInstanceRef.current) {
      dropdownInstanceRef.current.toggle()
    }
  }

  return (
    <div className="dropdown dropstart">
      <button
        ref={dropdownRef}
        className="btn btn-outline-secondary btn-sm dropdown-toggle"
        type="button"
        onClick={handleToggle}
        aria-expanded="false"
        style={{ minWidth: '80px' }}
      >
        Actions
      </button>
      <ul className="dropdown-menu">
        <li>
          <button
            className="dropdown-item"
            onClick={() => onViewDetails(event)}
          >
            📋 View Details
          </button>
        </li>
        <li>
          <button className="dropdown-item" onClick={() => onCopyId(event.id)}>
            🆔 Copy ID
          </button>
        </li>
        <li>
          <button
            className="dropdown-item"
            onClick={() => onCopyPayload(event)}
          >
            📄 Copy Payload JSON
          </button>
        </li>
        {event.user_id && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => onJumpToUser(event.user_id)}
            >
              👤 Jump to User
            </button>
          </li>
        )}
      </ul>
    </div>
  )
}

export default EventActionsDropdown
